import { EventProgramItem } from "@/types/EventProgramItem";
import { apiPost, apiGet, apiDelete, apiPut } from "../_apiShim";
import { withAiContentTool } from "@/lib/ai/aiContentQuery";

const BASE_PATH = "/api/v1/event-program/";

// ⚠ Every new item field a picker/panel reads MUST join this list — the
// backend's ?fields= projection drops anything not requested.
const PROGRAM_ITEM_FIELDS =
  "programItemId,eventId,title,description,startTime,endTime,location,speakerIds,capacity,sortIndex,createdAt," +
  "programmeId,sponsorIds,roomId,kind,offeringId,requiresRegistration,spansAllTracks,attachments";

export async function createEventProgramItem(
  eventId: string,
  item: EventProgramItem,
  /**
   * Art. 50 Abs. 2 KI-VO (J6): the AI lane behind this create — the JSON
   * transfer of a file from an AI-marked (or pre-wave) event sends
   * `template_import`. The body IS the entity, so it travels as
   * `?aiContentTool=`; the server stamps the event's guest-content mark.
   */
  aiContentTool?: string,
): Promise<string> {
  return await apiPost<string>(withAiContentTool(`${BASE_PATH}${eventId}`, aiContentTool), item, {
    "Cache-Control": "no-store",
  });
}

/**
 * Full program of an event. Deliberately THROWS on failure (unlike
 * getAdditionalFields' []-fallback) — consumers like the program-binding
 * refresher must distinguish "no items" from "fetch failed", otherwise a
 * transient error would wipe generated agenda children.
 */
export async function getEventProgramItems(
  eventId: string,
): Promise<EventProgramItem[]> {
  const url = `${BASE_PATH}${eventId}?fields=${encodeURIComponent(PROGRAM_ITEM_FIELDS)}`;
  return await apiGet<EventProgramItem[]>(url, { "Cache-Control": "no-store" });
}

export async function updateEventProgramItem(
  eventId: string,
  programItemId: string,
  item: Partial<EventProgramItem>,
  // Jsonb can't tell absent from null, so clearing the optional endTime
  // ("open-ended talk") / capacity ("uncapped again") / programmeId ("back
  // to unassigned") / roomId ("unlink the seating room") / offeringId
  // ("unbind the slotting container" — rejected on OFFERING by the backend)
  // travels as an explicit flag -> backend $unset.
  clearEndTime = false,
  clearCapacity = false,
  clearProgrammeId = false,
  clearRoomId = false,
  clearOfferingId = false,
): Promise<void> {
  const flags = [
    ...(clearEndTime ? ["clearEndTime=true"] : []),
    ...(clearCapacity ? ["clearCapacity=true"] : []),
    ...(clearProgrammeId ? ["clearProgrammeId=true"] : []),
    ...(clearRoomId ? ["clearRoomId=true"] : []),
    ...(clearOfferingId ? ["clearOfferingId=true"] : []),
  ];
  const suffix = flags.length > 0 ? `?${flags.join("&")}` : "";
  await apiPut<string>(`${BASE_PATH}${eventId}/${programItemId}${suffix}`, item, {
    "Cache-Control": "no-store",
  });
}

export async function deleteEventProgramItem(
  eventId: string,
  programItemId: string,
): Promise<void> {
  await apiDelete<string>(`${BASE_PATH}${eventId}/${programItemId}`, {
    "Cache-Control": "no-store",
  });
}
